import { leftReceptacle, leftSpout, topAtmosphere, type LevelConfig } from './portHelpers';
import type { MoleculeMix } from '../types';

export const LEVEL_5: LevelConfig = {
  step: 5,
  title: 'STEP FIVE',
  instructions: "Those gas bags won't fill themselves… place the lungs + diaphragm, connect the trachea to the atmosphere, and route CO₂-rich blood through the lungs to oxygenate it.",
  // The trachea (atmosphere ↔ lungs top) is mandatory — without it a student
  // could pipe the bidirectional atmosphere port straight into the O₂ wall
  // receptacle and skip the lungs entirely. See LevelConfig.requiredLinks.
  requiredLinks: [{ a: 's5-atmos', b: 's5-lungs-top' }],
  ports: [
    // Ceiling atmosphere port — bidirectional. Spouts O₂ down, accepts CO₂ up.
    topAtmosphere('s5-atmos', 10, ['oxygen']),
    // Bottom-left wall: CO₂-rich blood coming back from the body.
    leftSpout('s5-blood-in', 11, ['co2']),
    // Top-left wall: O₂-rich blood going out (where the heart will draw from).
    leftReceptacle('s5-blood-out', 4, ['oxygen']),
  ],
  organPool: [
    {
      id: 'lungs',
      art: 'lungs',
      cols: 5,
      rows: 3,
      // Lungs activate when the right blood output is emitting (i.e. they're
      // operational AND processing blood).
      activatesWhen: ['s5-lungs-right-out'],
      ports: [
        // TOP: bidirectional, the trachea endpoint. Cosmetic carries [oxygen]
        // for the porthole icon. Its emission is governed by `transform` —
        // emits [co2] only when blood with co2 is arriving AND the diaphragm
        // pulse is satisfied.
        {
          id: 's5-lungs-top',
          kind: 'bidirectional',
          facing: 'N',
          cellOffset: { dCol: 2, dRow: -1 },
          carries: ['oxygen'],
          coneFlipped: true,
          transform: (inputs) => {
            const left = inputs.get('s5-lungs-left-in') ?? [];
            const bottom = inputs.get('s5-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            const tracheaInputArriving = (inputs.get('s5-lungs-top') ?? []).length > 0;
            // Emit CO₂ only when operating, gas exchange is active (top has
            // any incoming flow = trachea connected), and CO₂ is in the blood.
            if (operating && tracheaInputArriving && left.includes('co2')) {
              return ['co2'];
            }
            return null;
          },
        },
        // LEFT INPUT: blood with CO₂ comes in here. acceptsAny so the input
        // accepts any blood mix; validIf gates whether it bursts.
        {
          id: 's5-lungs-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['co2'],
          acceptsAny: true,
          // Lungs operate only when the diaphragm is castellated-joined below
          // (sending a 'pulse'). Without the pulse, the receptacle is "broken"
          // and incoming pipes burst at arrival.
          validIf: (inputs) => {
            const bottom = inputs.get('s5-lungs-bottom-pulse') ?? [];
            return bottom.includes('pulse');
          },
        },
        // RIGHT OUTPUT: oxygenated blood goes out. Transform handles the
        // three states:
        //   - no diaphragm: emits nothing (organ broken — but the validIf on
        //     left-in already prevents arrival, so we won't actually reach here)
        //   - diaphragm + no trachea: passthrough left → right unchanged
        //   - diaphragm + trachea: (left − CO₂) ∪ {O₂}
        {
          id: 's5-lungs-right-out',
          kind: 'spout',
          facing: 'E',
          cellOffset: { dCol: 5, dRow: 0 },
          carries: ['oxygen'],
          transform: (inputs) => {
            const left = inputs.get('s5-lungs-left-in') ?? [];
            const bottom = inputs.get('s5-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            if (!operating) return null;
            if (left.length === 0) return null;
            // Trachea "connected" = any flow at the top port. Without that,
            // lungs become a straight passthrough (no gas exchange).
            const tracheaConnected = (inputs.get('s5-lungs-top') ?? []).length > 0;
            if (!tracheaConnected) {
              return left as MoleculeMix;
            }
            // Gas exchange: strip CO₂, add O₂.
            const out = new Set(left.filter((m) => m !== 'co2'));
            out.add('oxygen');
            return Array.from(out);
          },
        },
        // BOTTOM CASTELLATED: receives 'pulse' from the diaphragm via direct link.
        {
          id: 's5-lungs-bottom-pulse',
          kind: 'receptacle',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
    {
      id: 'diaphragm',
      art: 'diaphragm',
      cols: 3,
      rows: 2,
      ports: [
        // Single castellated spout at the top — unconditionally emits 'pulse'.
        // When placed directly below the lungs, the castellated joint forms
        // and the lungs detect the pulse via their bottom port.
        {
          id: 's5-diaphragm-top',
          kind: 'spout',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
  ],
};
